import { logger } from '@/lib/logger';

// import { proxy } from 'valtio'; // Not used directly
import { devtools } from 'valtio/utils';
import { createPageStore } from '../factories/pageStoreFactory';
import { createUIStore } from '../factories/uiStoreFactory';
import { createChatStore } from '../factories/chatStoreFactory';
import { createNotificationService } from '@/services/notificationService';
import { ChatStore, PageAction } from '../types';
import { initializeHelpers } from '../legacy/pageStoreHelpers';

// Legacy dispatch action types
type LegacyUIAction = 
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_ACTIVE_SECTION'; section: string }
  | { type: 'TOGGLE_MOBILE_MENU' }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SELECT_PAGE'; pageId: string }
  | { type: 'ADD_NOTIFICATION'; notification: { type: 'info' | 'success' | 'warning' | 'error'; message: string } }
  | { type: 'REMOVE_NOTIFICATION'; id: string }
  | { type: 'CLEAR_NOTIFICATIONS' }
  | { type: 'SET_PLAN_MODE'; enabled: boolean }
  | { type: 'START_IMPORT_PROCESSING'; requestId: string; fileType: string; message: string }
  | { type: 'UPDATE_IMPORT_PROGRESS'; message: string }
  | { type: 'FINISH_IMPORT_PROCESSING' };

// グローバルストアインスタンス（後方互換性のため）
const pageStoreInstance = createPageStore();
const uiStoreInstance = createUIStore({ notificationService: createNotificationService() });
const chatStoreInstance = createChatStore();

// 既存のグローバルストアを公開（非推奨）
export const pageStore = pageStoreInstance.store;
export const uiStore = uiStoreInstance.store;

// ChatStoreの型を満たすために、proxyオブジェクトに直接メソッドを追加
const chatStoreProxy = chatStoreInstance.store as ChatStore;
chatStoreProxy.addMessage = chatStoreInstance.actions.addMessage;
chatStoreProxy.updateMessage = chatStoreInstance.actions.updateMessage;
chatStoreProxy.setWsProgress = chatStoreInstance.actions.setWsProgress;
chatStoreProxy.setWsStatus = chatStoreInstance.actions.setWsStatus;
chatStoreProxy.setSourcePolicyState = chatStoreInstance.actions.setSourcePolicyState;
chatStoreProxy.setPolicyMismatchWarning = chatStoreInstance.actions.setPolicyMismatchWarning;

export const chatStore = chatStoreProxy;

// 追加のメソッドを後方互換性のために公開
const chatStoreExtended = chatStore as ChatStore & {
  removeMessage: typeof chatStoreInstance.actions.removeMessage;
  clearMessages: typeof chatStoreInstance.actions.clearMessages;
  setIsAIGenerating: typeof chatStoreInstance.actions.setIsAIGenerating;
  setPendingEditorUpdates: typeof chatStoreInstance.actions.setPendingEditorUpdates;
  addPendingEditorUpdate: typeof chatStoreInstance.actions.addPendingEditorUpdate;
  clearPendingEditorUpdates: typeof chatStoreInstance.actions.clearPendingEditorUpdates;
  setSourcePolicyState: typeof chatStoreInstance.actions.setSourcePolicyState;
  setPolicyMismatchWarning: typeof chatStoreInstance.actions.setPolicyMismatchWarning;
};
chatStoreExtended.removeMessage = chatStoreInstance.actions.removeMessage;
chatStoreExtended.clearMessages = chatStoreInstance.actions.clearMessages;
chatStoreExtended.setIsAIGenerating = chatStoreInstance.actions.setIsAIGenerating;
chatStoreExtended.setPendingEditorUpdates = chatStoreInstance.actions.setPendingEditorUpdates;
chatStoreExtended.addPendingEditorUpdate = chatStoreInstance.actions.addPendingEditorUpdate;
chatStoreExtended.clearPendingEditorUpdates = chatStoreInstance.actions.clearPendingEditorUpdates;
chatStoreExtended.setSourcePolicyState = chatStoreInstance.actions.setSourcePolicyState;
chatStoreExtended.setPolicyMismatchWarning = chatStoreInstance.actions.setPolicyMismatchWarning;

// 開発環境でのデバッグツール設定
// テスト環境では devtools を使用しない
if (process.env.NODE_ENV === 'development') {
  devtools(pageStore, { name: 'Page Store (Legacy)' });
  devtools(uiStore, { name: 'UI Store (Legacy)' });
  devtools(chatStore, { name: 'Chat Store (Legacy)' });
}

// 既存のdispatch関数を維持（非推奨）
export function dispatchPage(action: PageAction) {
  logger.warn('dispatchPage is deprecated. Use usePageStore hook instead.');
  
  switch (action.type) {
    case 'SET_PAGES':
      pageStoreInstance.actions.setPages(action.pages);
      break;
    case 'ADD_PAGE':
      pageStoreInstance.actions.addPage(action.page);
      break;
    case 'UPDATE_PAGE':
      pageStoreInstance.actions.updatePage(action.pageId, action.updates);
      break;
    case 'DELETE_PAGE':
      pageStoreInstance.actions.deletePage(action.pageId);
      break;
    case 'TOGGLE_EXPANDED':
      pageStoreInstance.actions.toggleExpanded(action.pageId);
      break;
    case 'TOGGLE_PAGE_EXPANSION':
      pageStoreInstance.actions.toggleExpanded(action.pageId);
      break;
    case 'START_EDITING':
      pageStoreInstance.actions.startEditing(action.pageId);
      break;
    case 'STOP_EDITING':
      pageStoreInstance.actions.stopEditing(action.pageId);
      break;
    case 'SET_LOADING':
      pageStoreInstance.actions.setLoading(action.isLoading);
      break;
    case 'SET_ERROR':
      pageStoreInstance.actions.setError(action.error);
      break;
    case 'CLEAR_ERROR':
      pageStoreInstance.actions.clearError();
      break;
    default:
      logger.warn(`Unknown action type: ${(action as { type: string }).type}`);
  }
}

// pageStoreHelpers の初期化
initializeHelpers(pageStore, dispatchPage);

// 既存のUI dispatch関数を維持（非推奨）
export function dispatch(action: LegacyUIAction) {
  logger.warn('dispatch is deprecated. Use useUIStore hook instead.');
  
  switch (action.type) {
    case 'TOGGLE_SIDEBAR':
      uiStoreInstance.actions.toggleSidebar();
      break;
    case 'SET_ACTIVE_SECTION':
      uiStoreInstance.actions.setActiveSection(action.section);
      break;
    case 'SELECT_PAGE':
      uiStore.selectedPageId = action.pageId;
      break;
    case 'ADD_NOTIFICATION':
      void uiStoreInstance.actions.addNotification(action.notification);
      break;
    case 'REMOVE_NOTIFICATION':
      uiStoreInstance.actions.removeNotification(action.id);
      break;
    case 'CLEAR_NOTIFICATIONS':
      uiStoreInstance.actions.clearAllNotifications();
      break;
    case 'SET_PLAN_MODE':
      uiStoreInstance.actions.setPlanMode(action.enabled);
      break;
    case 'START_IMPORT_PROCESSING':
      uiStoreInstance.actions.startImportProcessing(action.requestId, action.fileType, action.message);
      break;
    case 'UPDATE_IMPORT_PROGRESS':
      uiStoreInstance.actions.updateImportProgress(action.message);
      break;
    case 'FINISH_IMPORT_PROCESSING':
      uiStoreInstance.actions.finishImportProcessing();
      break;
    default:
      logger.warn(`Unknown action type: ${action.type}`);
  }
}

// グローバルストアのリセット関数（テスト用）
export function resetGlobalStores() {
  // ページストアのリセット
  pageStore.pages = {};
  pageStore.pageOrder = [];
  pageStore.editingPages = new Set();
  pageStore.expandedPages = new Set();
  pageStore.isLoading = false;
  pageStore.error = null;
  pageStore.currentEditingContent = {
    pageId: null,
    blocks: [],
    isDirty: false,
    lastSavedAt: null,
  };

  // UIストアのリセット
  uiStore.sidebar = { isOpen: true, activeSection: null };
  uiStore.notifications = [];
  uiStore.planMode = false;
  uiStore.selectedPageId = null;
  uiStore.importProcessing = {
    isProcessing: false,
    requestId: null,
    fileType: null,
    message: '',
    startedAt: null,
  };

  // チャットストアのリセット
  chatStore.messages = [];
  chatStore.isAIGenerating = false;
  chatStore.pendingEditorUpdates = [];
  chatStore.wsProgress = undefined;
  chatStore.wsStatus = undefined;
}

// 個別のリセット関数（テスト用）
export function resetPageStore() {
  pageStore.pages = {};
  pageStore.pageOrder = [];
  pageStore.editingPages = new Set();
  pageStore.expandedPages = new Set();
  pageStore.isLoading = false;
  pageStore.error = null;
  pageStore.currentEditingContent = {
    pageId: null,
    blocks: [],
    isDirty: false,
    lastSavedAt: null,
  };
}

export function resetUIStore() {
  uiStore.sidebar = { isOpen: true, activeSection: null };
  uiStore.notifications = [];
  uiStore.planMode = false;
  uiStore.selectedPageId = null;
  uiStore.importProcessing = {
    isProcessing: false,
    requestId: null,
    fileType: null,
    message: '',
    startedAt: null,
  };
}

export function resetChatStore() {
  chatStore.messages = [];
  chatStore.isAIGenerating = false;
  chatStore.pendingEditorUpdates = [];
  chatStore.wsProgress = undefined;
  chatStore.wsStatus = undefined;
}

// グローバルストアインスタンスへのアクセス（移行用）
export function getGlobalStoreInstances() {
  return {
    pageStore: pageStoreInstance,
    uiStore: uiStoreInstance,
    chatStore: chatStoreInstance,
  };
}
